import { Link, Navigate } from "react-router";
import { AppLayout } from "../components/layout/AppLayout";
import { useAuth0 } from "@auth0/auth0-react";
import { CHP_LESSON_DATA } from "../data/chp-lessons";
import { useParams } from "react-router";
import { useState, useEffect, useContext } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import Vimeo from "@u-wave/react-vimeo";
import type { CSSProperties } from "react";
import { H1, H2, H3 } from "../components/ui/headings";
import { Resources } from "../components/lesson/Resources";
import { Button } from "../components/ui/button";
import {
  NutritionPlanCallout,
  SupplementsCallout,
} from "../components/lesson/Callout";
import { ArrowLeftIcon } from "lucide-react";
import { NextLesson } from "../components/lesson/NextLesson";
import { UserContext } from "../lib/client/state/UserProvider";
import { CHP_LESSON_CONFIG } from "../data/lesson-config";

const embedContainerStyle: CSSProperties = {
  position: "relative",
  paddingBottom: "56.25%", // 16:9
  paddingTop: "25px",
  height: 0,
  marginTop: "25px",
};

const getLessonData = (program: string, lesson: string) => {
  if (program === "chp") {
    return CHP_LESSON_DATA.get(lesson);
  }
  return null;
};

export const LessonPage = () => {
  const { isAuthenticated } = useAuth0();
  const { lesson, program } = useParams();
  const [isReferencesExpanded, setIsReferencesExpanded] = useState(false);

  if (!isAuthenticated) {
    return <Navigate to="/" />;
  }

  const lessonData = getLessonData(program, lesson);

  if (!lessonData) {
    return <Navigate to="/lessons" />;
  }

  const context = useContext(UserContext);
  const user = context.user;
  const userProgram = user.programs.find((p) => p.program === program);
  const lastLessonCompleted = userProgram?.lastLessonCompleted;
  const currentLessonIndex = CHP_LESSON_CONFIG.findIndex(
    (l) => l.lessonCode === lesson
  );

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [user, program, lesson]);

  return (
    <AppLayout>
      <Link
        to="/lessons"
        className="text-muted-foreground inline-flex items-center gap-2 mb-4 hover:text-primary hover:underline"
      >
        <ArrowLeftIcon className="w-4 h-4" /> Back to Lesson List
      </Link>
      <Card>
        <CardHeader className="w-full xl:w-3/4">
          <p className="text-muted-foreground">{lessonData?.subtitle}</p>
          <CardTitle>
            <H1>{lessonData?.title}</H1>
          </CardTitle>
          <CardDescription className="text-base">
            {lessonData?.aboveVideoText}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col gap-4">
            <div style={embedContainerStyle}>
              {lessonData?.videoId && (
                // Related issue to the error that is thrown: https://github.com/u-wave/react-vimeo/issues/244
                <Vimeo
                  className="absolute top-0 left-0 w-full"
                  video={lessonData?.videoId}
                  responsive
                  onError={(error) =>
                    console.error("Vimeo player error:", error)
                  }
                  autoplay
                />
              )}
            </div>

            {lessonData?.secondaryVideos &&
              lessonData.secondaryVideos.length > 0 &&
              lessonData.secondaryVideos.map((video, index) => (
                <div key={index}>
                  <H2>{video.title}</H2>
                  <div style={embedContainerStyle}>
                    <Vimeo
                      className="absolute top-0 left-0 w-full"
                      video={video.videoId}
                      responsive
                      onError={(error) =>
                        console.error("Vimeo player error:", error)
                      }
                    />
                  </div>
                </div>
              ))}

            {program === "chp" && lesson === "L10" && <SupplementsCallout />}
            {program === "chp" && lesson === "L01" && <NutritionPlanCallout />}

            {lessonData?.belowVideoText && (
              <div
                dangerouslySetInnerHTML={{ __html: lessonData.belowVideoText }}
              />
            )}
            {lessonData?.mediaUrls && lessonData.mediaUrls.length > 0 && (
              <div className="w-full xl:w-3/4">
                {lessonData.mediaUrls.map((url, index) => (
                  <img key={index} src={url} alt="" className="w-full" />
                ))}
              </div>
            )}
            {lessonData?.bottomText && (
              <div
                dangerouslySetInnerHTML={{ __html: lessonData.bottomText }}
              />
            )}
            {lessonData?.keyTerms && lessonData.keyTerms.length > 0 && (
              <div className="w-full xl:w-3/4 flex flex-col gap-4">
                <H2>Key Terms</H2>
                <div className="flex flex-col gap-4">
                  {lessonData.keyTerms.map((term, index) => (
                    <div key={index}>
                      <p className="font-bold mb-1">{term.title}</p>
                      <p className="text-muted-foreground">{term.definition}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {lessonData?.resources && (
              <div className="w-full xl:w-3/4 flex flex-col gap-4">
                <H2>Resources</H2>
                {lessonData.resources.some(
                  (r) => r.type?.toLowerCase() === "article"
                ) && (
                  <Resources
                    resourceList={lessonData.resources.filter(
                      (resource) => resource.type?.toLowerCase() === "article"
                    )}
                  >
                    <H3>Articles</H3>
                  </Resources>
                )}
                {lessonData.resources.some(
                  (r) => r.type?.toLowerCase() === "book"
                ) && (
                  <Resources
                    resourceList={lessonData.resources.filter(
                      (resource) => resource.type?.toLowerCase() === "book"
                    )}
                  >
                    <H3>Books</H3>
                  </Resources>
                )}
                {lessonData.resources.some(
                  (r) => r.type?.toLowerCase() === "blog"
                ) && (
                  <Resources
                    resourceList={lessonData.resources.filter(
                      (resource) => resource.type?.toLowerCase() === "blog"
                    )}
                  >
                    <H3>Blog Posts</H3>
                  </Resources>
                )}
                {lessonData.resources.some(
                  (r) => r.type?.toLowerCase() === "video"
                ) && (
                  <Resources
                    resourceList={lessonData.resources.filter(
                      (resource) => resource.type?.toLowerCase() === "video"
                    )}
                  >
                    <H3>Videos</H3>
                  </Resources>
                )}
                {lessonData.resources.some(
                  (r) => r.type?.toLowerCase() === "product"
                ) && (
                  <Resources
                    resourceList={lessonData.resources.filter(
                      (resource) => resource.type?.toLowerCase() === "product"
                    )}
                  >
                    <H3>Products</H3>
                  </Resources>
                )}
                {lessonData.resources.some(
                  (r) => r.type?.toLowerCase() === "app"
                ) && (
                  <Resources
                    resourceList={lessonData.resources.filter(
                      (resource) => resource.type?.toLowerCase() === "app"
                    )}
                  >
                    <H3>Apps</H3>
                  </Resources>
                )}
              </div>
            )}
            {lessonData?.references && lessonData.references.length > 0 && (
              <div className="w-full xl:w-3/4 flex flex-col gap-4">
                <Button
                  variant="ghost"
                  className="w-fit p-0 h-auto hover:bg-transparent"
                  onClick={() => setIsReferencesExpanded(!isReferencesExpanded)}
                >
                  <H2 className="flex items-center gap-2">
                    References {isReferencesExpanded ? "▼" : "▶"}
                  </H2>
                </Button>
                {isReferencesExpanded && (
                  <div className="flex flex-col gap-2">
                    {lessonData.references.map((reference, index) => (
                      <div key={index} className="text-muted-foreground">
                        {reference}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
      {user &&
        lastLessonCompleted !== undefined &&
        lastLessonCompleted < 26 &&
        (currentLessonIndex === lastLessonCompleted + 1 ||
          lastLessonCompleted === null) &&
        lesson !== "L26" && (
          <Card className="w-full xl:w-3/4 mt-4 border-2 border-[#A966AA] bg-[#A966AA]/10">
            <CardHeader>
              <H2>Are you ready for the next lesson?</H2>
            </CardHeader>
            <CardContent>
              <NextLesson
                // @ts-expect-error user.user is not typed
                userId={user.user.id}
                program={program}
                lastLessonCompleted={lastLessonCompleted}
              />
            </CardContent>
          </Card>
        )}
      <div className="h-14"></div>
    </AppLayout>
  );
};
